/**
 * The PWA's adapter onto `@quickbee/assistant`
 * (docs/frontend/client-side-assistant-design.md §11, §12).
 *
 * ## Tier 0, and it cannot be promoted
 *
 * `preferredTier="lexical"`. These are savers on Indian mobile data, often on a
 * ₹9,000 Android, and the whole point of the tiering is that they pay **zero
 * megabytes** for a working assistant. `resolveTier` only ever downgrades a
 * preference, so nothing — not a config value, not a capable device, not a future
 * edit to the shell — can promote this mount to the 23 MB embedder. If the
 * semantic tier is ever wanted here it has to be an explicit, opt-in change on
 * this line, made deliberately.
 *
 * The corpus is the same 101-article bundle the shell serves, built into
 * `public/assistant/` by the same script.
 *
 * ## Mounted beside the routes, not inside them
 *
 * A sibling of `<AppRoutes />` under one `BrowserRouter`. The router itself never
 * unmounts, so the conversation survives every navigation; a widget rendered from
 * inside a page would be destroyed on the first tap.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@quickbee/auth';
import { AssistantHost } from '@quickbee/assistant';

/** The surface key the seed publishes the saver-facing FAQ under. */
const PWA_SURFACE = 'pwa';

export function PwaAssistantHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const tenantConfig = useAuthStore((s) => s.tenantConfig);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <AssistantHost
      preferredTier="lexical"
      /**
       * Signed-in savers only. This host is mounted above the router so it spans
       * every screen, and that includes Sign In, Register and OTP — where a
       * support widget offers help with an account the visitor does not have yet,
       * and where a raised ticket would have no requester behind it.
       */
      enabled={isAuthenticated}
      variant="mobile"
      locale={i18n.language?.split('-')[0] ?? 'en'}
      activeSurface={PWA_SURFACE}
      appName={tenantConfig?.appName || tenantConfig?.name || 'QuickBee'}
      surfaceLabel="your savings"
      userName={user?.name ?? null}
      currentPath={location.pathname}
      routeHint={(intent) =>
        intent === 'ACCOUNT_SPECIFIC' ? { label: 'Open your vault', path: '/vault' } : null
      }
      onNavigate={(path) => navigate(path)}
    />
  );
}
